import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { DEFAULT_LOCALE, type Locale } from "~/i18n/locales";
import type { StringKey } from "~/i18n/strings";
import { t as translate } from "~/i18n/t";

type LocaleContextData = {
  locale: Locale;
};

export const LocaleContext = createContext<LocaleContextData>({
  locale: DEFAULT_LOCALE,
});

interface LocaleProviderProps {
  locale: Locale;
  children: ReactNode;
}

export const LocaleProvider: FC<LocaleProviderProps> = ({
  locale,
  children,
}) => {
  const value = useMemo(() => ({ locale }), [locale]);
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export const useLocale = (): Locale => useContext(LocaleContext).locale;

/**
 * Returns a `t` function bound to the current locale so callers don't have to
 * thread it through manually. Prefer this over calling `t()` directly.
 *
 * The returned function has a stable reference per locale so callers can
 * safely put it in `useMemo`/`useCallback`/effect dep arrays.
 */
export const useT = () => {
  const locale = useLocale();
  return useCallback(
    (key: StringKey, vars?: Record<string, string | number>) =>
      translate(key, locale, vars),
    [locale],
  );
};
